import { Container } from '@amelio/ui-library';
import { useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { MeetingPreparationIllustration } from './components/MeetingPreparationIllustration';
import { PostReviewEmployeeInformation } from './components/PostReviewEmployeeInformation';
import { ResultsTabNavigation } from './components/ResultsTabNavigation';
import { useEvaluationPeriod } from '../Evaluation/hooks/useEvaluationPeriod';
import { getManagerAssessmentStepPath } from '../Evaluation/stepRoutes';
import { StepHeader } from '../shared/StepHeader/StepHeader';

import type {
    EvaluationLocationState,
    EvaluationPeriodOption,
    StepDefinition,
} from '../Evaluation/types';
import type { CompanyValueCardProps } from '@/pages/shared/ManagerAssessment/CompanyValueCard';
import type { EmployeeNote } from '@/types/employee-note';
import type { CompetencyGroupResult } from '@/types/manager-assessment/Competency';

import { useGetCompanyValues } from '@/api/company-values';
import { useGetCycleInstance } from '@/api/cycle-evaluation-services/cycle-instance';
import {
    useGetEvaluationPeriods,
    useGetManagerAssessmentCompetencyResults,
} from '@/api/cycle-evaluation-services/manager-assessment';
import { useGetUserById } from '@/api/directory';
import { useUpdateSkillProficiency } from '@/api/performance-profile';
import { useAmelioTranslations } from '@/hooks';
import { useAssessmentReadOnly } from '@/hooks/useAssessmentReadOnly';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { RouteName } from '@/navigation/types';
import {
    averageCompetencyLevelPercentage,
    buildCompanyValueCards,
    buildManagerAssessmentNotes,
    enrichProficiencyLevelsWithColor,
} from '@/pages/ManagerAssessment/utils/assessmentResultUtils';
import { useAssessmentNotes } from '@/pages/ManagerAssessment/utils/useAssessmentNotes';
import { getProfilePictureUrl } from '@/utils/user';

const NO_ASSESSMENT_NOTES: EmployeeNote[] = [];

export const ManagerAssessmentResultsPage = () => {
    const navigate = useNavigate();
    const location = useLocation<EvaluationLocationState | undefined>();
    const state = location.state;
    const { t } = useAmelioTranslations();

    const { data: evaluationPeriodsRaw = [] } = useGetEvaluationPeriods();

    // The id lives in the URL so a refresh keeps the context; location.state is only a hint.
    const { cycleInstanceId: cycleInstanceIdParam } = useParams<{ cycleInstanceId?: string }>();
    const cycleInstanceId = cycleInstanceIdParam ?? state?.cycleInstanceId;

    // Rehydrate from the CycleInstance so the page works on refresh / when navigation state is lost.
    const { data: cycleInstance, isLoading: isLoadingCycleInstance } = useGetCycleInstance(
        cycleInstanceId ?? '',
    );
    const { isReadOnly } = useAssessmentReadOnly(cycleInstance);
    const { data: companyValues = [] } = useGetCompanyValues();

    const employeeId =
        state?.employeeId ||
        cycleInstance?.reviewAudience?.reviewAudienceAssignments?.[0]?.assignedTo;

    // Update skill proficiency mutation
    const { mutateAsync: updateSkillProficiencyMutation, isPending: isUpdatingSkillProficiency } =
        useUpdateSkillProficiency(employeeId);

    // Get evaluation period data for period-based navigation
    const { selectedPeriod, customFromDate, customToDate } = useEvaluationPeriod(
        cycleInstanceId,
        cycleInstance,
        evaluationPeriodsRaw,
    );

    const rawReviewInstanceId =
        cycleInstance?.reviewInstances?.[0]?.id ??
        cycleInstance?.reviewAnswers?.[0]?.reviewInstanceId;
    const reviewInstanceId =
        rawReviewInstanceId && rawReviewInstanceId !== cycleInstanceId
            ? rawReviewInstanceId
            : undefined;

    const { data: employeeUser } = useGetUserById({ userId: employeeId ?? '' });
    const {
        data: competencyResults,
        isLoading: isLoadingCompetencyResults,
        isError: hasCompetencyResultsFailed,
    } = useGetManagerAssessmentCompetencyResults(cycleInstanceId);

    const evaluationPeriods = useMemo<EvaluationPeriodOption[]>(
        () =>
            evaluationPeriodsRaw.map((period) => ({
                value: period.id,
                label: period.name,
                from: period.startDate,
                to: period.endDate,
            })),
        [evaluationPeriodsRaw],
    );

    const proficiencyLevels = competencyResults?.proficiencyLevels ?? [];
    // The per-competency proficiency level is assigned at submission (from the calculated score for a
    // full-survey, or picked directly in a quick assessment), so competency-results already carries it.
    const competencyGroupResults: CompetencyGroupResult[] = competencyResults?.groups ?? [];

    const employeeFirstName = employeeUser?.firstName ?? '';
    const employeeLastName = employeeUser?.lastName ?? '';
    const employeeFullName = [employeeFirstName, employeeLastName].filter(Boolean).join(' ');

    const averageScore = useMemo(
        () =>
            // Backend-calculated global score, computed once at submission (competency-results).
            competencyResults?.globalScore ??
            averageCompetencyLevelPercentage(competencyGroupResults, proficiencyLevels),
        [competencyResults, competencyGroupResults, proficiencyLevels],
    );

    const competencyCountByLevel = useMemo(() => {
        const counts: Record<string, number> = {};
        competencyGroupResults.forEach((group) => {
            group.competencies.forEach((c) => {
                const levelId = c.proficiencyLevel?.id ?? c.lastAssessedProficiencyLevelId;
                if (levelId) {
                    counts[levelId] = (counts[levelId] || 0) + 1;
                }
            });
        });
        return counts;
    }, [competencyGroupResults]);

    const handleStepClick = useCallback(
        (step: StepDefinition) => {
            navigate({
                path: getManagerAssessmentStepPath(step.id, cycleInstanceId),
                state,
            });
        },
        [navigate, state, cycleInstanceId],
    );

    const companyValueCards = useMemo<CompanyValueCardProps[]>(
        () =>
            buildCompanyValueCards(
                cycleInstance,
                companyValues,
                proficiencyLevels,
                t('postReviewPrepareMeeting.companyValues.adherence'),
            ),
        [cycleInstance, companyValues, proficiencyLevels, t],
    );

    const valuesAlignmentPercentage = useMemo(() => {
        // The backend returns null when company values were skipped or not scored, and a number
        // (including 0) when at least one company value was observed. Respect that contract:
        // undefined means the result has not loaded yet.
        if (competencyResults === undefined) {
            return undefined;
        }
        if (competencyResults.valueAlignmentScore === null) {
            return null;
        }
        if (competencyResults.valueAlignmentScore === undefined) {
            return undefined;
        }
        return Math.round(competencyResults.valueAlignmentScore);
    }, [competencyResults]);

    const { data: assessmentNotesPage, status: assessmentNotesStatus } = useAssessmentNotes(
        employeeId,
        cycleInstanceId,
    );
    // A failed read still shows the comments answers kept from before question notes existed.
    const assessmentNotes =
        assessmentNotesStatus === 'error' ? NO_ASSESSMENT_NOTES : assessmentNotesPage?.items;
    const notes = useMemo(
        () => buildManagerAssessmentNotes(cycleInstance, assessmentNotes),
        [cycleInstance, assessmentNotes],
    );
    // The old comments come with the assessment: until it is read, no list of notes is complete.
    const notesStatus = isLoadingCycleInstance ? 'pending' : assessmentNotesStatus;

    const handlePrepareMeetingClick = useCallback(() => {
        navigate({
            path: `${RouteName.ManagerAssessmentPrepareMeeting}/${cycleInstanceId ?? ''}`,
            state,
        });
    }, [navigate, state, cycleInstanceId]);

    // Without this, a refused read falls through to `?? []` and the average of no competency is
    // zero: the page would show a score of 0 next to the employee's name, which reads as a mark
    // rather than as an absence. The server answers 404 both for a cycle that does not exist and
    // for one this user may not see, so the message names both instead of confirming it exists.
    if (hasCompetencyResultsFailed) {
        return (
            <Container
                direction={'col'}
                className={'flex-1'}
                dataTestId={'post-review-results-page'}
            >
                <div
                    className={'assessment-journey flex items-center justify-center py-10'}
                    data-testid={'post-review-results-error'}
                >
                    <p className={'text-error'}>{t('errors.notFoundOrInaccessibleMessage')}</p>
                </div>
            </Container>
        );
    }

    return (
        <Container
            direction={'col'}
            className={'flex-1'}
            dataTestId={'post-review-results-page'}
        >
            <StepHeader
                currentStep={2}
                employeeFirstName={employeeFirstName}
                onStepClick={handleStepClick}
            />
            <div className={'assessment-journey flex w-full flex-col gap-20 py-10'}>
                <PostReviewEmployeeInformation
                    reviewInstanceId={reviewInstanceId ?? ''}
                    cycleInstanceId={cycleInstanceId ?? ''}
                    disabled={isReadOnly}
                    employeeName={employeeFullName}
                    employeeUser={employeeUser}
                    averageScore={averageScore}
                    proficiencyLevels={enrichProficiencyLevelsWithColor(proficiencyLevels)}
                    employeeAvatarUrl={
                        getProfilePictureUrl(employeeUser?.profilePictureUrl) || undefined
                    }
                    competencyCountByLevel={competencyCountByLevel}
                    includeCompanyValues={cycleInstance?.includeCompanyValues ?? false}
                    valuesAlignmentPercentage={valuesAlignmentPercentage}
                    isLoading={isLoadingCompetencyResults || isLoadingCycleInstance}
                    overallPerformanceLevelId={competencyResults?.overallPerformanceLevelId}
                    overallPerformanceLevel={competencyResults?.overallPerformanceLevel}
                />

                <ResultsTabNavigation
                    reviewInstanceId={cycleInstanceIdParam ?? ''}
                    employeeId={employeeId ?? ''}
                    notes={notes}
                    notesStatus={notesStatus}
                    competencyGroup={competencyGroupResults}
                    proficiencyLevels={enrichProficiencyLevelsWithColor(proficiencyLevels)}
                    employeeName={employeeFirstName}
                    isReadOnly={isReadOnly}
                    selectedPeriod={selectedPeriod}
                    periods={evaluationPeriods}
                    fromDate={customFromDate}
                    toDate={customToDate}
                    updateSkillProficiency={updateSkillProficiencyMutation}
                    isUpdatingSkillProficiency={isUpdatingSkillProficiency}
                    companyValueCards={companyValueCards}
                />
            </div>

            <div className={'assessment-journey w-full py-10 md:my-8'}>
                <MeetingPreparationIllustration
                    employeeFirstName={employeeFirstName}
                    onPrepareMeetingClick={handlePrepareMeetingClick}
                />
            </div>
        </Container>
    );
};

export default ManagerAssessmentResultsPage;
